import { useEffect, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import { cn } from '@/lib/classNames';
import { OnboardAISuccess } from '@/resources/assets/images';
import { useBorderRotation } from '@/modules/sequencesRedesign/components/AiModal/hooks/useBorderRotation';
import { sendAnalytics } from '@/services/analyticsService';
import { RootState } from '@/rootReducer';
import { useSelector } from 'react-redux';
import { useOnboardingContext } from '../context/OnboardingContext';

export default function OnboardingSuccessModal({
	minimumTimePassed
}: {
	minimumTimePassed: boolean;
}) {
	const boxRef = useBorderRotation(minimumTimePassed, 80);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);
	const { onboardingData } = useOnboardingContext();
	const [showConfetti, setShowConfetti] = useState(true);
	const [opacity, setOpacity] = useState(1);
	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});

	useEffect(() => {
		// Recalculate size on mount
		const handleResize = () =>
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		window.addEventListener('resize', handleResize);
		handleResize();

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const hasTrackedRef = useRef(false);

	useEffect(() => {
		if (!hasTrackedRef.current && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Onboarding Flow - Campaign Generated Successfully',
				properties: {
					onboardingData
				}
			});
			hasTrackedRef.current = true;
		}
	}, [user.email, onboardingData]);

	useEffect(() => {
		if (!minimumTimePassed) return;

		const fadeTimer = setTimeout(() => {
			const fadeDuration = 2000;
			const fadeInterval = 50;
			const fadeStep = fadeInterval / fadeDuration;

			const fadeOut = setInterval(() => {
				setOpacity(prev => {
					const next = prev - fadeStep;
					return next <= 0 ? 0 : next;
				});
			}, fadeInterval);

			setTimeout(() => {
				clearInterval(fadeOut);
				setShowConfetti(false);
			}, fadeDuration);
		}, 3000);

		return () => clearTimeout(fadeTimer);
	}, [minimumTimePassed]);

	return (
		<div className="h-full border-l border-dm-gray">
			<div className="relative flex w-full justify-center px-4 py-6 text-white sm:pt-20">
				{showConfetti && (
					<Confetti
						width={dimensions.width}
						height={dimensions.height}
						opacity={opacity}
						tweenDuration={3000}
					/>
				)}

				<div
					ref={boxRef}
					data-is-loading={minimumTimePassed}
					style={
						{
							'--angle': '0deg',
							'--border-color':
								'linear-gradient(var(--angle), #BC82F3, #F5B9EA, #8D99FF, #AA6EEE, #FF6778, #FFBA71, #C686FF)',
							'--bg-color': 'linear-gradient(#212121, #212121)'
						} as React.CSSProperties
					}
					className={cn(
						'w-full max-w-[43.75rem] rounded-2xl border-2 border-transparent p-[2px]',
						"data-[is-loading='true']:border-[#0000]",
						"data-[is-loading='true']:[background:padding-box_var(--bg-color),border-box_var(--border-color)]"
					)}
				>
					<div className="flex h-full flex-col gap-8 overflow-hidden rounded-2xl bg-dm-black3/50 px-6 pb-8 pt-8 sm:h-[24.5rem] sm:gap-6 sm:pb-14 sm:pl-[3.25rem] sm:pr-6 sm:pt-8">
						<div className="flex flex-col items-center gap-4 font-barlow text-[1.25rem] font-semibold leading-[1.5rem]">
							<OnboardAISuccess />
							<div className="flex flex-col gap-[0.375rem] text-center text-white">
								<p className="text-dm-gold">Congratulations!</p>
								<p>You are all set!</p>
								<span className="text-[3.5rem] leading-[3.5rem]">
									🎊
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
